import omit from 'lodash/omit';

import { adoptFeature, describe, fillStyle, lineFeature, polygonFeature, strokeStyle } from '../../common/geojson';
import { INTERNALS } from '../../common/metadata';

/**
 * Layers shared by the three coastline modules - Shoreline, Shore-R and Shore-S - which measure
 * the same things and so export the same shapes.
 * @module COASTLINE_GEOJSON
 */

/**
 * @function areaFeature
 * @description The area of interest, as a polygon.
 * @param {Array<Array<Number>>} [coordinates] - Ring of `[lng, lat]` pairs.
 * @memberof module:COASTLINE_GEOJSON
 * @returns {Object|null} The feature, or `null` when no area was drawn.
 */
export const areaFeature = (coordinates) =>
  polygonFeature(coordinates, 'area_of_interest', { ...describe('Area of interest'), ...fillStyle('#00a391', 0) });

/**
 * @function baselineFeature
 * @description The baseline every measurement is taken from, as a line.
 * @param {Array<Array<Number>>} [coordinates] - Baseline vertices.
 * @memberof module:COASTLINE_GEOJSON
 * @returns {Object|null} The feature, or `null` when no baseline was drawn.
 */
export const baselineFeature = (coordinates) =>
  lineFeature(coordinates, 'baseline', { ...describe('Baseline'), ...strokeStyle('#ffd700', { width: 3 }) });

/**
 * @function transectFeature
 * @description One transect as a line, carrying whatever the module measured along it.
 * Takes the transect either as the analysis holds it (`{id, properties}`) or already flattened,
 * since the modules differ on that, and copies every property through rather than naming them:
 * each module measures a slightly different set - Shore-R adds smoothed rates, for one - and a
 * fixed list would quietly drop whatever it did not know about.
 * The per-date series stays as the text the analysis produced. Those measurements have no geometry
 * of their own - a distance along a transect is not a place - so there is nothing to give them as
 * features, and a column per date would differ between analyses.
 * @param {Object} transect - A transect, in either shape.
 * @memberof module:COASTLINE_GEOJSON
 * @returns {Object|null} The feature, or `null` when its endpoints are unusable.
 */
export const transectFeature = (transect) => {
  if (!transect) return null;

  const properties = transect.properties
    ? { id: transect.id, ...omit(transect.properties, [INTERNALS]) }
    : transect;

  const rate = Number.isFinite(Number(properties.lrr)) ? `LRR ${Number(properties.lrr).toFixed(2)} m/year` : undefined;

  return lineFeature(
    [
      [properties.longStart, properties.latStart],
      [properties.longEnd, properties.latEnd],
    ],
    'transects',
    {
      ...properties,
      ...describe(`Transect ${properties.id ?? ''}`.trim(), rate),
      ...strokeStyle('#ff7043', { width: 2 }),
    },
  );
};

/**
 * @constant COASTLINE_NAMES
 * @description What each set of coastlines is called, by the layer it goes out as.
 * @memberof module:COASTLINE_GEOJSON
 */
const COASTLINE_NAMES = {
  shorelines: 'Shoreline',
  modeled_coastlines: 'Modelled coastline',
  reference_coastlines: 'Reference coastline',
};

/**
 * @constant COASTLINE_COLORS
 * @description A colour per set, so the modelled and the reference coastlines can be told apart on
 * sight in a viewer that reads simplestyle.
 * @memberof module:COASTLINE_GEOJSON
 */
const COASTLINE_COLORS = {
  shorelines: '#1e88e5',
  modeled_coastlines: '#8e24aa',
  reference_coastlines: '#1e88e5',
};

/**
 * @function coastlineDate
 * @description The date a coastline belongs to, whichever of the several names the modules store it
 * under. A coastline without its date is just a line; the date is the whole point of a series.
 * @param {Object} [properties] - The feature's own properties.
 * @memberof module:COASTLINE_GEOJSON
 * @returns {String|undefined} The date as text, or nothing when there is none.
 */
const coastlineDate = (properties = {}) => {
  const raw = properties.date ?? properties.time ?? properties.year ?? properties['system:time_start'];
  if (raw === undefined || raw === null || raw === '') return undefined;

  const asNumber = Number(raw);
  const parsed = Number.isFinite(asNumber) && String(raw).length > 4 ? new Date(asNumber) : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? String(raw) : parsed.toISOString().slice(0, 10);
};

/**
 * @function coastlineFeatures
 * @description The extracted coastlines, one feature per date, taken as they already are.
 * @param {Array<Object>|Object} [source] - Either a list of GeoJSON features or a FeatureCollection.
 * @param {String} [layer='shorelines'] - Layer name, for modules that carry more than one set.
 * @memberof module:COASTLINE_GEOJSON
 * @returns {Array<Object|null>} The features.
 */
export const coastlineFeatures = (source, layer = 'shorelines') => {
  const features = Array.isArray(source) ? source : source?.features;

  return (features || []).map((feature, index) => {
    const when = coastlineDate(feature?.properties);
    const label = `${COASTLINE_NAMES[layer] || 'Coastline'} ${when || index + 1}`;

    return adoptFeature(feature, layer, {
      feature_index: index,
      ...describe(label, when ? `Coastline extracted for ${when}` : undefined),
      ...strokeStyle(COASTLINE_COLORS[layer] || '#1e88e5', { width: 2 }),
    });
  });
};
